import React, { useState, useEffect } from 'react';
import './CustomerManager.css';

const CustomerManager = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, [currentPage, statusFilter, searchTerm]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        status: statusFilter,
        search: searchTerm
      });

      const response = await fetch(`/api/customer-admin/customers?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers);
        setTotalPages(data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/customer-admin/dashboard-stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleStatusChange = async (customerId, newStatus, notes = '') => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/customer-admin/customers/${customerId}/permission`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus, notes })
      });

      if (response.ok) {
        fetchCustomers();
        fetchStats();
        setSelectedCustomer(null);
      }
    } catch (error) {
      console.error('Error updating customer status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'status-pending', text: 'Pendiente' },
      approved: { class: 'status-approved', text: 'Aprobado' },
      rejected: { class: 'status-rejected', text: 'Rechazado' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="customer-manager">
      <div className="customer-manager-header">
        <h2>Gestión de Clientes</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Clientes</h3>
            <span className="stat-number">{stats.totalCustomers || 0}</span>
          </div>
          <div className="stat-card">
            <h3>Pendientes</h3>
            <span className="stat-number pending">{stats.customersByStatus?.pending || 0}</span>
          </div>
          <div className="stat-card">
            <h3>Aprobados</h3>
            <span className="stat-number approved">{stats.customersByStatus?.approved || 0}</span>
          </div>
          <div className="stat-card">
            <h3>Rechazados</h3>
            <span className="stat-number rejected">{stats.customersByStatus?.rejected || 0}</span>
          </div>
        </div>
      </div>

      <div className="customer-manager-filters">
        <div className="filter-group">
          <label>Filtrar por estado:</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobados</option>
            <option value="rejected">Rechazados</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Buscar:</label>
          <input
            type="text"
            placeholder="Email, nombre, empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="customer-manager-content">
        {loading ? (
          <div className="loading">Cargando clientes...</div>
        ) : (
          <div className="customers-table">
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Email</th>
                  <th>Empresa</th>
                  <th>Estado</th>
                  <th>Registro</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <div className="customer-info">
                        <strong>{customer.first_name} {customer.last_name}</strong>
                      </div>
                    </td>
                    <td>{customer.email}</td>
                    <td>{customer.company || '-'}</td>
                    <td>{getStatusBadge(customer.permission_status)}</td>
                    <td>{formatDate(customer.created_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-view"
                          onClick={() => setSelectedCustomer(customer)}
                        >
                          Ver
                        </button>
                        {customer.permission_status === 'pending' && (
                          <>
                            <button 
                              className="btn-approve"
                              onClick={() => handleStatusChange(customer.id, 'approved')}
                            >
                              Aprobar
                            </button>
                            <button 
                              className="btn-reject"
                              onClick={() => handleStatusChange(customer.id, 'rejected')}
                            >
                              Rechazar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span>Página {currentPage} de {totalPages}</span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {selectedCustomer && (
        <CustomerDetailModal 
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
};

const CustomerDetailModal = ({ customer, onClose, onStatusChange }) => {
  const [permissionRequests, setPermissionRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchCustomerDetails();
  }, [customer.id]);

  const fetchCustomerDetails = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/customer-admin/customers/${customer.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPermissionRequests(data.permissionRequests || []);
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = (newStatus) => {
    onStatusChange(customer.id, newStatus, notes);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Detalles del Cliente</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="customer-details">
            <h4>Información Personal</h4>
            <div className="detail-grid">
              <div><strong>Nombre:</strong> {customer.first_name} {customer.last_name}</div>
              <div><strong>Email:</strong> {customer.email}</div>
              <div><strong>Empresa:</strong> {customer.company || 'No especificada'}</div>
              <div><strong>Teléfono:</strong> {customer.phone || 'No especificado'}</div>
              <div><strong>Dirección:</strong> {customer.address || 'No especificada'}</div>
              <div><strong>Ciudad:</strong> {customer.city || 'No especificada'}</div>
              <div><strong>Estado:</strong> {customer.state || 'No especificado'}</div>
              <div><strong>País:</strong> {customer.country || 'No especificado'}</div>
              <div><strong>Registro:</strong> {new Date(customer.created_at).toLocaleDateString('es-MX')}</div>
            </div>

            <h4>Estado de Permisos</h4>
            <div className="permission-status">
              <span className={`status-badge ${customer.permission_status}`}>
                {customer.permission_status === 'approved' ? 'Aprobado' : 
                 customer.permission_status === 'pending' ? 'Pendiente' : 'Rechazado'}
              </span>
              {customer.approved_at && (
                <div className="approval-info">
                  Aprobado el: {new Date(customer.approved_at).toLocaleDateString('es-MX')}
                </div>
              )}
            </div>

            {customer.permission_status === 'pending' && (
              <div className="permission-actions">
                <h4>Acciones</h4>
                <div className="action-group">
                  <label>Notas (opcional):</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Agregar notas sobre la decisión..."
                    rows="3"
                  />
                </div>
                <div className="action-buttons">
                  <button 
                    className="btn-approve"
                    onClick={() => handleStatusUpdate('approved')}
                  >
                    Aprobar Acceso
                  </button>
                  <button 
                    className="btn-reject"
                    onClick={() => handleStatusUpdate('rejected')}
                  >
                    Rechazar Acceso
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerManager;
